import {
  Banner,
  Box,
  Checkbox,
  Divider,
  DropdownButtonContext,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import {
  ChangeEvent,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { aiApi } from 'apis/ai';
import { AiBanner } from 'components/aiBanner';
import { AiLoadingState } from 'components/aiLoadingState';
import { CopilotDropdownActionContext } from 'components/copilotDropdown';
import { HtmlRenderer } from 'components/htmlRenderer';
import { sanitizationProfile, useEditorContext } from 'utils/useEditorContext';
import { LanguageSelectionListValue } from 'utils/useLanguageSelectionList';
import { useTranslateText } from 'utils/useTranslateText';

import {
  CopilotDropdownFooter,
  CopilotDropdownHeader,
} from '../../../copilotDropdown/components';
import { EditorCopilotDropdownContentBox } from '../contentBox';

import {
  CopilotLanguageInputSelectionList,
  CopilotTranslateOnLanguageChange,
} from './components';

const additionalFeedback = {
  feature: 'translate',
};

/** A subject field translated along with the editor, e.g. an email's. */
export type SubjectField = {
  readonly get: () => string;
  readonly set: (value: string) => void;
};

type SubjectResult = {
  readonly language: string;
  /** Undefined when the translation failed. */
  readonly text?: string;
};

type CopilotDropdownTranslateProps = {
  readonly initialText?: string;
  /** The conversation this editor belongs to, if any. */
  readonly csid?: string;
  readonly subjectField?: SubjectField;
};

/**
 * Represents the component that translates selected text or entire content of
 * the editor.
 */
export const EditorCopilotDropdownTranslate = memo(
  function EditorCopilotDropdownTranslate({
    initialText,
    csid,
    subjectField,
  }: CopilotDropdownTranslateProps) {
    const { onBack, actionsComputed } = useContext(
      CopilotDropdownActionContext,
    );
    const actionComputed = actionsComputed.translate;
    const { insertBelow, isEmpty, replace, getSelectedText, hasSelection } =
      useEditorContext();
    const dropdownContext = useContext(DropdownButtonContext);
    const [translate, { data }] =
      aiApi.useLazyReadStreamAiTextTranslationQuery();
    const [selectedLanguage, setSelectedLanguage] =
      useState<LanguageSelectionListValue>();

    const [subject] = useState(() =>
      subjectField && !hasSelection() ? subjectField.get().trim() : '',
    );
    const [includeSubject, setIncludeSubject] = useState(true);
    const [translateSubject] = useTranslateText();
    const [subjectResult, setSubjectResult] = useState<SubjectResult>();
    const selectedLanguageName = useRef<string>();
    const isSubjectIncluded = !!subject && includeSubject;
    const currentSubjectResult =
      subjectResult?.language === selectedLanguage?.name
        ? subjectResult
        : undefined;
    const translatedSubject = currentSubjectResult?.text;
    const isSubjectFailed = !!currentSubjectResult && !translatedSubject;
    const isSubjectPending =
      isSubjectIncluded && !!selectedLanguage && !currentSubjectResult;

    const translatedText = useMemo(() => {
      return data?.text ?? undefined;
    }, [data?.text]);

    const requestSubjectTranslation = useCallback(
      async (language: string) => {
        let text: string | undefined;

        try {
          ({ translatedText: text } = await translateSubject({
            language,
            text: subject,
            contentType: 'text',
            direction: 'outbound',
            ...(csid ? { csid } : {}),
          }));
        } catch {
          text = undefined;
        }

        // Replies for a language that's no longer selected are dropped.
        if (language === selectedLanguageName.current) {
          setSubjectResult({ language, text });
        }
      },
      [csid, subject, translateSubject],
    );

    const content = useMemo(() => {
      if (!isEmpty && !initialText && !selectedLanguage) {
        return (
          <AiBanner>
            Choose a language to view the translation preview displayed here.
          </AiBanner>
        );
      }

      if (!data && initialText) {
        return <AiBanner>{initialText}</AiBanner>;
      }

      if (data?.meta?.isLoading) {
        return <AiLoadingState label="" />;
      }

      if (data?.meta?.isError) {
        return (
          <Banner
            variant="fail"
            description={
              <span>
                An error occurred while translating into the selected language.
              </span>
            }
          />
        );
      }

      if (isEmpty && !data?.meta?.isStreaming) {
        return (
          <Banner
            variant="warning"
            description={<span>There is no text to translate.</span>}
          />
        );
      }

      if (translatedText) {
        return (
          <FlexBox flexDirection="column" gap="250">
            {isSubjectIncluded && (
              <>
                <Box>
                  <Text.Paragraph variant="3" color="secondary">
                    Subject
                  </Text.Paragraph>
                  <Text.Paragraph
                    variant="2"
                    color={
                      isSubjectFailed
                        ? 'error'
                        : translatedSubject
                          ? undefined
                          : 'tertiary'
                    }
                  >
                    {isSubjectFailed ? (
                      <>
                        Couldn&apos;t translate the subject.
                        <br />
                        Try again or edit it by hand.
                      </>
                    ) : (
                      (translatedSubject ?? subject)
                    )}
                  </Text.Paragraph>
                </Box>
                <Divider />
              </>
            )}
            <HtmlRenderer
              sanitizationProfileType={sanitizationProfile}
              html={translatedText}
            />
          </FlexBox>
        );
      }
    }, [
      data,
      initialText,
      isEmpty,
      isSubjectIncluded,
      selectedLanguage,
      subject,
      isSubjectFailed,
      translatedSubject,
      translatedText,
    ]);

    /** Handles the change event of languages selection list. */
    const handleLanguageChange = useCallback<CopilotTranslateOnLanguageChange>(
      async (event) => {
        setSelectedLanguage(event.selectedLanguage);
        selectedLanguageName.current = event.selectedLanguage?.name;

        if (!event.selectedLanguage?.name) {
          return;
        }

        const text = getSelectedText();

        if (isSubjectIncluded) {
          void requestSubjectTranslation(event.selectedLanguage.name);
        }

        try {
          await translate({
            language: event.selectedLanguage.name,
            text,
            ...(csid ? { csid } : {}),
            direction: 'outbound',
          });
        } catch {
          // Already handled via asyncStatus
        }
      },
      [
        csid,
        getSelectedText,
        isSubjectIncluded,
        requestSubjectTranslation,
        translate,
      ],
    );

    const handleIncludeSubjectChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setIncludeSubject(checked);

        if (checked && selectedLanguage?.name && !currentSubjectResult) {
          void requestSubjectTranslation(selectedLanguage.name);
        }
      },
      [currentSubjectResult, requestSubjectTranslation, selectedLanguage?.name],
    );

    const applySubject = useCallback(() => {
      // A subject edited while the panel was open is kept.
      if (
        isSubjectIncluded &&
        translatedSubject &&
        subjectField?.get().trim() === subject
      ) {
        subjectField.set(translatedSubject);
      }
    }, [isSubjectIncluded, subject, subjectField, translatedSubject]);

    const handleInsertBelow = useCallback(() => {
      if (!translatedText) {
        return;
      }

      insertBelow(translatedText);
      applySubject();

      dropdownContext.closeDropdown();

      ampli.agentCopilotTextInsertBelowClicked({
        key: 'translate',
        csid,
      });
    }, [translatedText, dropdownContext, insertBelow, applySubject]);

    const handleReplace = useCallback(() => {
      if (!translatedText) {
        return;
      }

      replace(translatedText);
      applySubject();

      dropdownContext.closeDropdown();

      ampli.agentCopilotTextReplaceClicked({
        key: 'translate',
        csid,
      });
    }, [translatedText, dropdownContext, replace, applySubject]);

    return (
      <Box
        // Streamed content must not resize the panel.
        style={{
          maxWidth: 768,
          minWidth: 540,
          width: subject ? 640 : undefined,
        }}
      >
        <CopilotDropdownHeader
          title={actionComputed.title}
          onBack={onBack}
          feedbackId={data?.feedback_id ?? ''}
          additionalFeedback={additionalFeedback}
          actions={
            subject ? (
              <FlexBox as="label" gap="200" alignItems="center">
                <Checkbox
                  name="include-subject"
                  checked={includeSubject}
                  onChange={handleIncludeSubjectChange}
                />
                <Text.Paragraph variant="3" wrap="nowrap">
                  Include subject
                </Text.Paragraph>
              </FlexBox>
            ) : undefined
          }
        >
          <CopilotLanguageInputSelectionList
            onLanguageChange={handleLanguageChange}
            selectedLanguageCode={selectedLanguage?.code}
            disabled={isEmpty}
          />
        </CopilotDropdownHeader>

        <EditorCopilotDropdownContentBox isEmphasized={!!translatedText}>
          {content}
        </EditorCopilotDropdownContentBox>

        <Divider />

        <CopilotDropdownFooter
          handleInsertBelow={handleInsertBelow}
          handleReplace={handleReplace}
          disableInsertBelow={!translatedText || isSubjectPending}
          disableReplace={!translatedText || isSubjectPending}
          draftContent={translatedText}
          disableImproveReply={!translatedText}
        />
      </Box>
    );
  },
);
